import React from 'react';
import ReactDOM from 'react-dom/client';
import AudioPlayer from '../features/MediaLibrary/AudioPlayer';

// ===========================================
// TOKEN CREATION AND PARSING
// ===========================================

// Create audio token for content
export const createAudioToken = (audioId, audioName = '') => {
  return `[AUDIO:${audioId}:${audioName}]`;
};

// Parse audio token to extract ID and name
export const parseAudioToken = (token) => {
  const match = token.match(/\[AUDIO:([^:]+):([^\]]*)\]/);
  if (match) {
    return {
      audioId: match[1],
      audioName: match[2] || ''
    };
  }
  return null;
};

// Convert old URL format to token (for backward compatibility)
export const convertUrlToToken = async (content) => {
  if (!content || typeof content !== 'string') return content;
  
  // Pattern to match audio URLs in content
  const urlPattern = /https:\/\/firebasestorage\.googleapis\.com\/[^\s<>"]+\.mp3[^\s<>"]*/g;
  
  let convertedContent = content;
  const matches = content.match(urlPattern);
  
  if (matches) {
    // Import AudioService dynamically to avoid circular imports
    const { default: AudioService } = await import('../features/MediaLibrary/AudioService');
    
    for (const url of matches) {
      try {
        // Try to find existing audio asset with this URL
        const allAssets = await AudioService.getAudioAssets();
        const matchingAsset = allAssets.find(asset => asset.url === url);
        
        if (matchingAsset) {
          const token = createAudioToken(matchingAsset.id, matchingAsset.name);
          convertedContent = convertedContent.replace(url, token);
        }
      } catch (error) {
        console.warn('Could not convert URL to token:', url, error);
        // Keep original URL if conversion fails
      }
    }
  }
  
  return convertedContent;
};

// ===========================================
// CONTENT PROCESSING
// ===========================================

// Replace audio tokens in content with AudioPlayer components
export const renderAudioTokens = (containerElement, showFileName = false) => {
  if (!containerElement) return;
   
  // Skip processing speech bubbles
  if (containerElement.closest('.no-audio-tokens') || 
      containerElement.classList.contains('no-audio-tokens')) {
    return;
  }

  // Define the token pattern - handles trailing dots/ellipsis
  // NOTE: We create a NEW regex for each check to avoid state issues with the /g flag
  const tokenPattern = /\[AUDIO:([^:\]]+):([^\]]*)\]/;
  
  // Create tree walker to find text nodes
  const walker = document.createTreeWalker(
    containerElement,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) {
    if (tokenPattern.test(node.textContent)) {
      textNodes.push(node);
    }
  }

  // Process each text node that contains tokens
  textNodes.forEach(textNode => {
    const content = textNode.textContent;
    // Create a FRESH regex with /g flag for matchAll (avoids state pollution)
    const tokenPatternGlobal = /\[AUDIO:([^:\]]+):([^\]]*)\]/g;
    const tokens = [...content.matchAll(tokenPatternGlobal)];
    
    if (tokens.length === 0) return;

    // Create document fragment to replace the text node
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    tokens.forEach(match => {
      const [fullMatch, audioId, audioName] = match;
      const startIndex = match.index;

      // Clean the audioId and audioName (remove trailing dots/ellipsis)
      const cleanAudioId = audioId.replace(/[.…]+$/, '').trim();
      const cleanAudioName = audioName.replace(/[.…]+$/, '').trim();

      // Add text before the token
      if (startIndex > lastIndex) {
        const beforeText = content.substring(lastIndex, startIndex);
        fragment.appendChild(document.createTextNode(beforeText));
      }

      // Create container for AudioPlayer
      const audioContainer = document.createElement('div');
      audioContainer.className = 'audio-player-container';
      audioContainer.style.cssText = 'display: inline-block; margin: 4px; vertical-align: middle;';
      audioContainer.setAttribute('data-audio-id', cleanAudioId);
      
      // Mount React AudioPlayer component
      try {
        const root = ReactDOM.createRoot(audioContainer);
        root.render(
          <AudioPlayer 
            audioId={cleanAudioId}
            showFileName={showFileName}
          />
        );
        
        // Store root for cleanup
        audioContainer._reactRoot = root;
      } catch (error) {
        console.error('Error mounting AudioPlayer:', error);
        // Fallback: show the token as text
        audioContainer.textContent = `🎵 ${cleanAudioName || 'Audio'}`;
      }

      fragment.appendChild(audioContainer);
      lastIndex = startIndex + fullMatch.length;
    });

    // Add remaining text after the last token
    if (lastIndex < content.length) {
      const afterText = content.substring(lastIndex);
      fragment.appendChild(document.createTextNode(afterText));
    }

    // Replace the original text node
    textNode.parentNode.replaceChild(fragment, textNode);
  });
};

// Clean up AudioPlayer components in container
export const cleanupAudioPlayers = (containerElement) => {
  if (!containerElement) return;

  const audioContainers = containerElement.querySelectorAll('.audio-player-container');
  audioContainers.forEach(container => {
    try {
      if (container._reactRoot) {
        container._reactRoot.unmount();
        delete container._reactRoot;
      }
    } catch (error) {
      console.warn('Error cleaning up AudioPlayer:', error);
    }
  });
};

// ===========================================
// EDITOR INSERTION HELPERS
// ===========================================

// Insert audio token in HTML editor
export const insertAudioInHTML = (audioId, audioName = '') => {
  return createAudioToken(audioId, audioName);
};

// Insert audio token in Quill editor
export const insertAudioInQuill = (quillRef, audioId, audioName = '') => {
  if (!quillRef.current) {
    throw new Error('Quill editor not available');
  }
  
  const quill = quillRef.current.getEditor();
  quill.focus();
  
  const range = quill.getSelection(true) || { index: 0, length: 0 };
  const audioToken = createAudioToken(audioId, audioName);
  
  // Insert token without adding newline (which might cause formatting issues)
  quill.insertText(range.index, audioToken);
  quill.setSelection(range.index + audioToken.length);
};

// ===========================================
// CONTENT MIGRATION HELPERS
// ===========================================

// Prepare content for saving (convert URLs to tokens)
export const prepareContentForSaving = async (content) => {
  if (!content) return content;
  
  if (typeof content === 'string') {
    // Skip URL conversion for interactive image content
    try {
      const parsed = JSON.parse(content);
      if (parsed.hotspots) {
        // This is interactive image content - don't convert URLs
        return content;
      }
    } catch (e) {
      // Not JSON, continue with normal processing
    }
    
    return await convertUrlToToken(content);
  }
  
  if (Array.isArray(content)) {
    return Promise.all(content.map(item => prepareContentForSaving(item)));
  }
  
  if (typeof content === 'object') {
    const converted = {};
    for (const [key, value] of Object.entries(content)) {
      converted[key] = await prepareContentForSaving(value);
    }
    return converted;
  }
  
  return content;
};